// Getting the user IN, not merely seated (2026-09-14). A seat that streams is not a seat the user is inside:
// the measured failure is a slot whose UE never leaves `Map_Login` because the INIT never landed, while a
// perfectly healthy 30 fps of that login screen keeps every watchdog we had happy — the media watchdog counts
// frames, the server's D-53 rescue asks whether the slot is dark, and both answer "fine".
//
// The day this module comes from: of the eight seats event 034126a8 sold on 2026-09-14, four never reached the
// venue. One (14:05:57 → 14:12:12) had the page reload onto its seat, the server correctly restarted the UE for
// it (`lease.ue-reset` 14:06:20 → `admin.slot-restart` 14:06:28), the browser re-attached at 14:06:32 — and then
// the login map was streamed at the user for three and a half minutes until they closed the tab, after which the
// seat idled out to its TTL. Three others held a seat for 5–13 minutes with no viewer ever connecting, because
// the page's wait for a streamer to come back had no cap at all.
//
// So this module owns the two things the page was missing:
//   - a verdict ladder for "seated, streaming, still not in" — re-send the INIT, then hand the seat back, then
//     stop and say so, rather than streaming a login screen until the user gives up;
//   - the words for every wait. The second failure here is silence: a user who cannot tell a 40 s wait from a
//     hang refreshes, and a refresh costs a UE restart plus another minute — so every state says what is
//     happening, how long it should take, that the seat is theirs, and whether they need to do anything.

/** How long the page waits for a streamer to come back after a restart before writing that seat off. */
export const STREAMER_RETURN_MS = 60_000;

/** How long after the INIT went out we wait for the venue before sending it a second time. */
export const VENUE_NUDGE_MS = 45_000;

/** …and how long before the seat itself is judged unusable. Measured entries reach the venue in 8–20 s. */
export const VENUE_GIVE_UP_MS = 90_000;

/** Seats a single visit may hand back for a stalled entry before the page stops and tells the user. */
export const MAX_ENTRY_RESCUES = 2;

/** How often the entry ladder is looked at. The verdicts are second-grained; this only has to be finer. */
export const ENTRY_WATCH_POLL_MS = 1000;

export interface IEntryWatchState {
  /** When the current seat was adopted — the clock for an entry that never even gets its INIT out. */
  seatedAt: number | null;
  /** When the INIT for the CURRENT peer went out, or null while it has not. */
  initSentAt: number | null;
  /** A second INIT has already been sent on this seat — the nudge is once per seat, never a loop. */
  nudged: boolean;
  /** Seats already handed back for a stalled entry in this visit. */
  rescues: number;
}

export type EntryAction = 'wait' | 'nudge' | 'move' | 'fail';

export interface IEntryVerdict {
  action: EntryAction;
  why: string;
}

const sec = (ms: number): number => Math.round(ms / 1000);

// The ladder, as a pure function of two clocks and a counter. `wait` is the answer for everything that is
// merely slow: the INIT not out yet, the venue still loading, the nudge still settling.
export function entryVerdict(
  state: IEntryWatchState,
  now: number,
): IEntryVerdict {
  // No INIT yet. This is the branch the three 2026-09-14 seats that never saw a viewer fell through: the peer
  // never came up, so no media watchdog was ever armed, and the page renewed that seat for 5–13 minutes. The
  // seat's own age answers it — whatever is failing upstream (no peer, no data channel, a mint that will not
  // come back), a seat that cannot be INIT-ed is worth handing back for a fresh one.
  if (state.initSentAt === null) {
    if (state.seatedAt === null) {
      return { action: 'wait', why: 'no seat yet' };
    }

    const held = now - state.seatedAt;

    if (held < VENUE_GIVE_UP_MS) {
      return {
        action: 'wait',
        why: `${sec(held)} s on this seat, the INIT has not gone out yet`,
      };
    }

    return state.rescues >= MAX_ENTRY_RESCUES
      ? {
          action: 'fail',
          why: `${state.rescues} seats in a row could not be started`,
        }
      : {
          action: 'move',
          why: `nothing could be delivered to this seat in ${sec(
            held,
          )} s — handing it back`,
        };
  }

  const waited = now - state.initSentAt;

  if (waited < VENUE_NUDGE_MS) {
    return { action: 'wait', why: `${sec(waited)} s since the INIT` };
  }

  if (!state.nudged) {
    return {
      action: 'nudge',
      why: `no venue ${sec(waited)} s after the INIT — sending it again`,
    };
  }

  if (waited < VENUE_GIVE_UP_MS) {
    return {
      action: 'wait',
      why: `${sec(waited)} s since the INIT, re-sent once (gives up at ${sec(
        VENUE_GIVE_UP_MS,
      )} s)`,
    };
  }

  if (state.rescues >= MAX_ENTRY_RESCUES) {
    return {
      action: 'fail',
      why: `${state.rescues} seats in a row never loaded the venue`,
    };
  }

  return {
    action: 'move',
    why: `no venue ${sec(
      waited,
    )} s after the INIT and a re-send — handing the seat back`,
  };
}

// The streamer-return cap. Until 2026-09-14 this wait was bound only to the gateway's streamer list, which is
// right while the UE is coming back and unbounded when it never does — three seats that day were held 5–13
// minutes on exactly this. A minute is long enough for every restart we have measured (the server quotes ~75 s
// for a UE reset, and the streamer re-registers well inside it) and short enough that a machine which is not
// coming back is replaced before the user decides we are broken.
export function streamerReturnVerdict(
  waitingSince: number | null,
  now: number,
): IEntryVerdict {
  if (waitingSince === null) {
    return { action: 'wait', why: 'not waiting on a streamer' };
  }

  const waited = now - waitingSince;

  return waited < STREAMER_RETURN_MS
    ? {
        action: 'wait',
        why: `streamer away for ${sec(waited)} s (gives up at ${sec(
          STREAMER_RETURN_MS,
        )} s)`,
      }
    : {
        action: 'move',
        why: `the streamer did not come back within ${sec(
          STREAMER_RETURN_MS,
        )} s — taking another machine`,
      };
}

export type EntryStage =
  | 'searching'
  | 'starting'
  | 'connecting'
  | 'engine-restart'
  | 'entering'
  | 'nudging'
  | 'moving'
  | 'stalled';

export interface IEntryNotice {
  stage: EntryStage;
  /** What is happening right now, in the words the screen puts in its headline. */
  title: string;
  /** One short sentence when the headline is not the whole answer; absent when it is. */
  detail?: string;
  /** A clause about the seat, when it is worth saying; absent leaves the screen's standing hint. */
  reassurance?: string;
}

export interface IEntryNoticeInput {
  stage: EntryStage;
  /** Which seat of this visit we are on, 1-based — only shown once it is more than the first. */
  attempt?: number;
}

const HELD_SEAT = 'Your seat is reserved.';

const KEPT_SEAT = 'Your seat is kept.';

// Short on purpose (operator's verdict on the first build of this screen: cut it hard or drop it). The
// first draft explained itself at length — how machines are picked, that there was no warm one, that clicking
// and refreshing would not help — and long copy under a progress bar reads as apology, which is the opposite
// of the confidence it was written for. So: one plain sentence for what is happening, at most a short clause
// for the seat, and nothing at all where the headline already says it. No internal vocabulary either — "no
// machine was warm" is our word for our problem, and the user has never heard it.
//
// `detail` and `reassurance` are both optional: an omitted reassurance leaves the screen's own standing hint
// in place rather than filling the space for the sake of it.
export function describeEntry(input: IEntryNoticeInput): IEntryNotice {
  const { stage, attempt } = input;
  const nth =
    attempt && attempt > 1 ? ` (${attempt} of ${MAX_ENTRY_RESCUES + 1})` : '';

  switch (stage) {
    case 'searching':
      return {
        stage,
        title: 'Finding you a machine',
        reassurance: 'Your seat is reserved as soon as we find one.',
      };

    case 'starting':
      return {
        stage,
        title: 'Starting your machine',
        detail: 'A dedicated GPU is booting for this session.',
        reassurance: HELD_SEAT,
      };

    case 'connecting':
      return {
        stage,
        title: 'Connecting your stream',
        reassurance: HELD_SEAT,
      };

    case 'engine-restart':
      return {
        stage,
        title: 'Restarting your session',
        detail: `Your machine is restarting the experience — up to ${sec(
          STREAMER_RETURN_MS,
        )} seconds, then we move you to another one.`,
        reassurance: KEPT_SEAT,
      };

    case 'entering':
      return {
        stage,
        title: 'Loading your space',
      };

    case 'nudging':
      return {
        stage,
        title: 'Still loading your space',
        detail: 'Longer than usual — we have re-sent your session.',
        reassurance: KEPT_SEAT,
      };

    case 'moving':
      return {
        stage,
        title: `Moving you to another machine${nth}`,
        detail: 'That one did not load your space.',
        reassurance: `${KEPT_SEAT} About a minute.`,
      };

    case 'stalled':
    default:
      return {
        stage: 'stalled',
        title: 'We could not get you in',
        detail: 'Every machine we tried failed to load your space.',
        reassurance: 'This is on us, not your connection — try again.',
      };
  }
}
